import { useState } from 'react';
import {
  auth,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from '../firebase.js';
import { Modal, submitForm } from './Modal.jsx';
import { FormField } from './FormField.jsx';
import { checkPassword, policySummary } from '../utils/passwordPolicy.js';

// Modal that re-authenticates the current user with their existing password,
// then sets a new one. Matches the server-side password policy configured
// via the Firebase Identity Toolkit admin API.
export function ChangePasswordModal({ open, onClose, onSuccess }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const nextCheck = checkPassword(next);
  const canSubmit =
    current.length > 0 &&
    next.length > 0 &&
    confirm.length > 0 &&
    next === confirm &&
    nextCheck.ok;

  function reset() {
    setCurrent(''); setNext(''); setConfirm(''); setErr(null); setBusy(false);
  }
  function close() { reset(); onClose(); }

  async function submit(e) {
    e.preventDefault();
    setErr(null);
    if (next !== confirm) { setErr('New passwords do not match.'); return; }
    if (!nextCheck.ok) { setErr(`New password needs: ${nextCheck.failures.join(', ')}.`); return; }
    if (current === next) { setErr('New password must be different from current.'); return; }

    const user = auth.currentUser;
    if (!user?.email) { setErr('No signed-in user.'); return; }

    setBusy(true);
    try {
      const cred = EmailAuthProvider.credential(user.email, current);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, next);
      reset();
      onSuccess?.();
      onClose();
    } catch (e) {
      const code = e?.code || '';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setErr('Current password is incorrect.');
      } else if (code === 'auth/weak-password' || code === 'auth/password-does-not-meet-requirements') {
        setErr('New password does not meet the password policy.');
      } else if (code === 'auth/requires-recent-login') {
        setErr('Session too old. Sign out and sign back in, then try again.');
      } else if (code === 'auth/too-many-requests') {
        setErr('Too many attempts. Try again in a few minutes.');
      } else {
        setErr(e.message || 'Could not update password.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      eyebrow="Security"
      title="Change password"
      size="md"
      footer={
        <>
          <button type="button" onClick={close} className="btn-ghost">Cancel</button>
          <button
            type="button"
            onClick={() => submitForm('change-password-form')}
            className="btn-primary"
            disabled={busy || !canSubmit}
          >
            {busy ? 'Updating…' : 'Update password'}
          </button>
        </>
      }
    >
      {err && <div className="mb-4 px-3 py-2 rounded-md bg-danger/10 text-danger text-sm">{err}</div>}
      <form id="change-password-form" onSubmit={submit} className="space-y-4">
        <FormField label="Current password" required>
          <input
            type="password"
            className="input"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            autoFocus
            required
          />
        </FormField>
        <FormField label="New password" required hint={policySummary()}>
          <input
            type="password"
            className="input"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            required
          />
        </FormField>
        {next.length > 0 && (
          <ul className="text-xs space-y-0.5 -mt-2">
            <RequirementRow ok={nextCheck.checks.length}>At least 12 characters</RequirementRow>
            <RequirementRow ok={nextCheck.checks.upper}>One uppercase letter</RequirementRow>
            <RequirementRow ok={nextCheck.checks.lower}>One lowercase letter</RequirementRow>
            <RequirementRow ok={nextCheck.checks.digit}>One number</RequirementRow>
            <RequirementRow ok={nextCheck.checks.symbol}>One symbol</RequirementRow>
          </ul>
        )}
        <FormField label="Confirm new password" required>
          <input
            type="password"
            className="input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
          {confirm.length > 0 && next !== confirm && (
            <div className="text-xs text-danger mt-1">Does not match.</div>
          )}
        </FormField>
      </form>
    </Modal>
  );
}

function RequirementRow({ ok, children }) {
  return (
    <li className={ok ? 'text-success' : 'text-ink-3'}>
      {ok ? '✓' : '·'} {children}
    </li>
  );
}
